import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { DEFAULT_CURRENCY, majorToMinor } from "@/core/utils/func/money-minor";
import { useCreateSavingsVault } from "@/features/savings/api/use-create-savings-vault";
import type { CreateSavingsVaultRequestDto } from "@/features/savings/api/dto";
import {
  createSavingsVaultSchema,
  type SavingsVaultFormValues,
} from "@/features/savings/schemas/savings-vault.schema";
import { useWalletSelectOptions } from "@/features/shared/api";

const EMPTY_VALUES: SavingsVaultFormValues = {
  name: "",
  goalAmount: undefined,
  mainWalletId: "",
};

/**
 * Ouverture d'un coffre d'épargne (`POST /SavingsVaults/add`, idempotent) —
 * le coffre est adossé à l'un des portefeuilles principaux du titulaire et
 * en reprend la devise. Preview `MutationConfirmPreview` (opération sans
 * frais) avant l'appel API : aucun appel au simple clic.
 */
export function SavingsVaultFormDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  // Les previews attendent une signature `(key: string) => string`.
  const tSavings = (key: string) => t(key as TFunctionType);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, le schéma zod traduit est reconstruit à chaque changement de langue (convention login.schema.ts).
  const schema = useMemo(() => createSavingsVaultSchema(), [i18n.language]);

  const createVault = useCreateSavingsVault();
  const pending = createVault.isPending;
  const [preview, setPreview] = useState<CreateSavingsVaultRequestDto | null>(
    null
  );
  const { options: walletOptions, isLoading: walletsLoading } =
    useWalletSelectOptions(open);

  // Radix démonte le contenu du Dialog à la fermeture : à chaque ouverture,
  // le formulaire repart des valeurs vides.
  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      const payload: CreateSavingsVaultRequestDto = {
        name: parsed.data.name,
        // 0 / absent = sans objectif (goalAmountMinor omis).
        goalAmountMinor:
          parsed.data.goalAmount && parsed.data.goalAmount > 0
            ? majorToMinor(parsed.data.goalAmount, DEFAULT_CURRENCY)
            : null,
        mainWalletId: parsed.data.mainWalletId,
      };
      setPreview(payload);
    },
  });

  const fieldError =
    (name: keyof SavingsVaultFormValues) =>
    ({ value }: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t("savings.vaults.create")}</DialogTitle>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field name="name" validators={{ onChange: fieldError("name") }}>
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="savings-vault-name">
                    {t("savings.vaults.form.name")}
                  </Label>
                  <Input
                    id="savings-vault-name"
                    maxLength={80}
                    placeholder={t("savings.vaults.form.namePlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="goalAmount"
              validators={{ onChange: fieldError("goalAmount") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="savings-vault-goal">
                    {t("savings.vaults.form.goalAmount")}
                  </Label>
                  <Input
                    id="savings-vault-goal"
                    type="number"
                    min={0}
                    className="notranslate"
                    placeholder={t("savings.vaults.form.goalAmountPlaceholder")}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) =>
                      field.handleChange(
                        Number.isNaN(e.target.valueAsNumber)
                          ? undefined
                          : e.target.valueAsNumber
                      )
                    }
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="mainWalletId"
              validators={{ onChange: fieldError("mainWalletId") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="savings-vault-wallet">
                    {t("savings.vaults.form.mainWalletId")}
                  </Label>
                  <EntitySearchableSelect
                    id="savings-vault-wallet"
                    options={walletOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={t("savings.vaults.form.mainWalletIdPlaceholder")}
                    isLoading={walletsLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <DialogFooter className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPreview(null);
        }}
        data={preview}
        title={t("common.mutationPreview.createTitle")}
        description={t("common.mutationPreview.createDescription")}
        t={tSavings}
        pending={pending}
        onConfirm={() => {
          if (!preview) return;
          createVault.mutate(preview, {
            onSuccess: () => {
              notifSilence(t("savings.vaults.feedback.created"));
              setPreview(null);
              onOpenChange(false);
            },
            onError: (error) =>
              notifSilenceError(apiErrorHandler(error) || error.message),
          });
        }}
      />
    </>
  );
}
